import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import Box from "@mui/material/Box";
import {
    Button,
    TextField,
    CircularProgress,
    TablePagination,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import * as XLSX from "xlsx";
import { toast } from "react-toastify";
import PageHeader1 from "../../components/common/PageHeader1";
import { useSearchParams } from "react-router-dom";
import { getIntParam, getStringParam, setIfTruthy } from "../../utils/urlState";

function ReferralReport() {
    const [searchParams, setSearchParams] = useSearchParams();
    const didInitUrlSyncRef = React.useRef(false);
    const [data, setData] = useState([]);
    const [status, setStatus] = useState("loading");
    const [paginationModel, setPaginationModel] = useState({
        page: getIntParam(searchParams, "page", 0),
        pageSize: getIntParam(searchParams, "pageSize", 10),
    });
    const [paginationDetail, setPaginationDetail] = useState({});
    const [search, setSearch] = useState(getStringParam(searchParams, "search", ""));
    const [startDate, setStartDate] = useState(getStringParam(searchParams, "startDate", ""));
    const [endDate, setEndDate] = useState(getStringParam(searchParams, "endDate", ""));
    const [isExporting, setIsExporting] = useState(false);

    // Keep URL in sync so Back restores pagination/filters.
    useEffect(() => {
        if (!didInitUrlSyncRef.current) {
            didInitUrlSyncRef.current = true;
            return;
        }
        const params = new URLSearchParams();
        params.set("page", String(paginationModel.page));
        params.set("pageSize", String(paginationModel.pageSize));
        setIfTruthy(params, "search", search);
        setIfTruthy(params, "startDate", startDate);
        setIfTruthy(params, "endDate", endDate);
        setSearchParams(params, { replace: true });
    }, [paginationModel.page, paginationModel.pageSize, search, startDate, endDate, setSearchParams]);

    const fetchReport = async () => {
        try {
            setStatus("loading");
            const token = localStorage.getItem("astroVani-admin-token");

            const params = new URLSearchParams({
                page: paginationModel.page + 1,
                limit: paginationModel.pageSize,
            });

            if (search) params.append("search", search);
            if (startDate) params.append("startDate", startDate);
            if (endDate) params.append("endDate", endDate);

            const response = await axios.get(
                `${process.env.REACT_APP_BASEURL}/admin/referral-report?${params.toString()}`,
                { headers: { Authorization: token } }
            );

            setData(response.data?.data || []);
            setPaginationDetail(response.data?.paginationDetail || {});
            setStatus("success");
        } catch (err) {
            toast.error("Failed to fetch referral report");
            setStatus("error");
        }
    };

    useEffect(() => {
        fetchReport();
        // eslint-disable-next-line
    }, [
        paginationModel.page,
        paginationModel.pageSize,
        search,
        startDate,
        endDate,
    ]);

    /* =========================
       EXPORT TO EXCEL
    ========================= */
    const exportToExcel = async () => {
        setIsExporting(true);
        try {
            const token = localStorage.getItem("astroVani-admin-token");

            const params = new URLSearchParams({});

            if (search) params.append("search", search);
            if (startDate) params.append("startDate", startDate);
            if (endDate) params.append("endDate", endDate);

            const response = await axios.get(
                `${process.env.REACT_APP_BASEURL}/admin/referralExport?${params.toString()}`,
                { headers: { Authorization: token } }
            );

            const exportData = response.data?.data.map((item, index) => ({
                "S.No": index + 1,
                "User Name": item.fullName,
                "Mobile": item.mobileNumber,
                "Referral Code": item.referralCode,
                "Referred By": item.referredBy,
                "Referred User Reward": item.referredUserRewardAmount,
                "Referrer Reward": item.referrerRewardAmount,
                "Reward Status": item.referrerRewardStatus,
                "Referrer Name": item.referrerName,
                "Referrer Mobile": item.referrerMobile,
                "Created At": new Date(item.createdAt).toLocaleString(),
            }));

            const worksheet = XLSX.utils.json_to_sheet(exportData);
            const workbook = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(workbook, worksheet, "Referral Report");
            XLSX.writeFile(workbook, "referral_report.xlsx");

            toast.success("Referral report exported");
        } catch {
            toast.error("Export failed");
        } finally {
            setIsExporting(false);
        }
    };

    const handleReset = () => {
        setSearch("");
        setStartDate("");
        setEndDate("");
        setPaginationModel({ page: 0, pageSize: 10 });
    };

    /* =========================
       TABLE
    ========================= */
    const rows = useMemo(
        () =>
            data.map((item, index) => ({
                id: item._id,
                sno: paginationModel.page * paginationModel.pageSize + index + 1,
                ...item,
                createdAt: new Date(item.createdAt),
            })),
        [data, paginationModel]
    );

    const columns = [
        { field: "sno", headerName: "S.No", width: 70 },
        { field: "fullName", headerName: "User Name", width: 180 },
        { field: "mobileNumber", headerName: "Mobile", width: 150 },
        { field: "referralCode", headerName: "Referral Code", width: 150 },
        { field: "referredBy", headerName: "Referred By", width: 150 },
        {
            field: "referredUserRewardAmount",
            headerName: "User Reward",
            width: 130,
        },
        {
            field: "referrerRewardAmount",
            headerName: "Referrer Reward",
            width: 140,
        },
        {
            field: "referrerRewardStatus",
            headerName: "Reward Status",
            width: 130,
        },
        { field: "referrerName", headerName: "Referrer Name", width: 180 },
        { field: "referrerMobile", headerName: "Referrer Mobile", width: 150 },
        {
            field: "createdAt",
            headerName: "Created At",
            width: 180,
            type: "dateTime",
        },
    ];

    return (
        <div className="container-xxl">
            <PageHeader1 pagetitle="Referral Report" />

            {/* Filters */}
            <div className="card mb-3">
                <div className="card-body">
                    <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                        <TextField
                            label="Search"
                            size="small"
                            value={search}
                            onChange={(e) => {
                                setSearch(e.target.value);
                                setPaginationModel((p) => ({ ...p, page: 0 }));
                            }}
                        />

                        <TextField
                            label="Start Date"
                            type="date"
                            size="small"
                            value={startDate}
                            InputLabelProps={{ shrink: true }}
                            onChange={(e) => setStartDate(e.target.value)}
                        />

                        <TextField
                            label="End Date"
                            type="date"
                            size="small"
                            value={endDate}
                            InputLabelProps={{ shrink: true }}
                            onChange={(e) => setEndDate(e.target.value)}
                        />

                        <Button variant="outlined" onClick={handleReset}>
                            Reset
                        </Button>

                        <Button
                            variant="contained"
                            sx={{ ml: "auto" }}
                            onClick={exportToExcel}
                            disabled={isExporting}
                            startIcon={isExporting && <CircularProgress size={16} />}
                        >
                            Export
                        </Button>
                    </Box>
                </div>
            </div>

            {/* Table */}
            <div className="card">
                <div className="card-body p-0">
                    <DataGrid
                        rows={rows}
                        columns={columns}
                        loading={status === "loading"}
                        hideFooterPagination
                        autoHeight
                    />
                    <TablePagination
                        component="div"
                        count={paginationDetail?.totalDocs || 0}
                        page={paginationModel.page}
                        onPageChange={(e, p) =>
                            setPaginationModel((prev) => ({ ...prev, page: p }))
                        }
                        rowsPerPage={paginationModel.pageSize}
                        onRowsPerPageChange={(e) =>
                            setPaginationModel({ page: 0, pageSize: +e.target.value })
                        }
                    />
                </div>
            </div>
        </div>
    );
}

export default ReferralReport;
